<script>
import RawData from "@/components/widgets/raw-data.vue";
export default {
  name: 'FundSourcesCards',
  components: {
    RawData
  },
  props: {
    fundSources: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentSlide: 0,
      touchStartX: 0,
      touchEndX: 0,
      slideWidth: 640, // Doubled from 320px to 640px (600px card + 40px gap)
      isMouseOver: false
    }
  },
  mounted() {
    // Auto-scroll is removed from mounted
  },
  beforeDestroy() {
    // No need to stop auto-scroll anymore
  },
  methods: {
    formatPercentage(value, total) {
      if (!total || total === 0) return '0.00%'
      const percentage = (value / total) * 100
      return `${percentage.toFixed(2)}%`
    },
    checkExpiry(fundSource) {
      if (!fundSource.hasExpiry) return true
      return fundSource.hasExpiry ? (new Date() < new Date(fundSource.expiry)) : true
    },
    nextSlide() {
      if (this.currentSlide < this.fundSources.length - 1) {
        this.currentSlide++
      }
    },
    prevSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--
      }
    },
    goToSlide(index) {
      this.currentSlide = index
    },
    handleMouseEnter() {
      this.isMouseOver = true
    },
    handleMouseLeave() {
      this.isMouseOver = false
    },
    handleWheel(e) {
      if (!this.isMouseOver) return
      
      e.preventDefault()
      
      // Determine scroll direction
      const delta = e.deltaY || e.deltaX
      
      if (delta > 0) {
        // Scroll down/right - go to next slide
        this.nextSlide()
      } else if (delta < 0) {
        // Scroll up/left - go to previous slide
        this.prevSlide()
      }
    },
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
    },
    handleTouchMove(e) {
      e.preventDefault()
    },
    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].clientX
      this.handleSwipe()
    },
    handleSwipe() {
      const swipeThreshold = 50
      const swipeDistance = this.touchStartX - this.touchEndX
      
      if (Math.abs(swipeDistance) > swipeThreshold) {
        if (swipeDistance > 0) {
          this.nextSlide()
        } else {
          this.prevSlide()
        }
      }
    },
    generateReport(type, fundSource) {
      this.$emit('generate-report', { type, fundSource })
    },
  }
}
</script>
<template>
  <div 
    class="fund-sources-slider"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @wheel="handleWheel"
  >
    <div class="slider-container">
      <div 
        class="slider-wrapper" 
        :style="{ transform: `translateX(-${currentSlide * slideWidth}px)` }"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div 
          v-for="(fundSource, index) in fundSources" 
          :key="fundSource.id || index"
          class="card-slide"
          :class="{ 'active': index === currentSlide }"
        >
          <div class="fund-card position-relative">
            <RawData :data="fundSource" v-if="$store.state.decrypt.isAdmin()" />
            <div class="card-header">
              <div class="fund-info">
                <h3 class="fund-name">{{ fundSource.sourceName || fundSource.name }}</h3>
                <span class="fund-type">{{ fundSource.description || 'For testing only' }}</span>
              </div>
              <div class="card-actions d-flex gap-1">
                <button class="action-btn" title="View Utilization Schedule" @click="$emit('view-details', fundSource)">
                  <i class="mdi mdi-calendar font-size-24"></i>
                </button>
                <b-dropdown
                  variant="link"
                  class="vertical-align-unset"
                  toggle-class="text-decoration-none p-0 action-btn"
                  menu-class="dropdown-menu-end"
                  no-caret
                >
                    <template #button-content>
                        <button class="action-btn" title="Reports">
                          <i class="mdi mdi-file-document font-size-24 text-purple"></i>
                        </button>
                    </template>
                    <b-dropdown-item @click="generateReport('expenditure', fundSource)">
                      <i class="bx bx-receipt font-size-18 align-middle me-2"></i>
                      Expenditure Allotment
                    </b-dropdown-item>
                    <b-dropdown-item @click="generateReport('utilization', fundSource)">
                      <i class="bx bx-money font-size-18 align-middle me-2"></i>
                      Utilization Report
                    </b-dropdown-item>
                    <b-dropdown-item @click="generateReport('obligation', fundSource)">
                      <i class="bx bx-poll font-size-18 align-middle me-2"></i>
                      Obligation Report
                    </b-dropdown-item>
                    <b-dropdown-item @click="generateReport('disbursement', fundSource)">
                      <i class="mdi mdi-book-arrow-right-outline font-size-18 align-middle me-2"></i>
                      Disbursement Report
                    </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>

            <!-- Expiry Date -->
            <div class="expiry-section">
              <span class="expiry-label">Expiry Date</span>
              <div class="expiry-date">
                <span class="date-text">{{ formatDate(new Date(fundSource.expiry) || new Date(fundSource.expiryDate)) }}</span>
                <span 
                  class="status-badge" 
                  :class="checkExpiry(fundSource) ? 'status-active' : 'status-expired'"
                >
                  {{ checkExpiry(fundSource) ? 'ACTIVE' : 'EXPIRED' }}
                </span>
              </div>
            </div>

            <!-- Account Information -->
            <div class="account-section">
              <span class="account-label">Account</span>
              <div class="account-info">
                <div class="bank-info">
                  <span class="bank-name">Bank: {{ fundSource.bankName || 'LANDBANK' }}</span>
                  <span class="account-number">Account: {{ fundSource.accountNumber || '254875448' }}</span>
                </div>
              </div>
            </div>

            <!-- Actual Cash -->
            <div class="cash-section">
              <span class="cash-label">Actual Cash</span>
              <div class="cash-amount">{{ formatCurrency(fundSource.actualCashBalance) }}</div>
            </div>

            <!-- Transactions Section -->
            <div class="transactions-section">
              <div class="transaction-stats">
                <div class="stat-item" @click="$emit('view-obligations', fundSource)">
                  <span class="stat-label">Total Obligations</span>
                  <span class="stat-value obligations">{{ formatPercentage(fundSource.totalObligations, fundSource.balance) }}</span>
                  <span class="stat-amount">{{ formatCurrency(fundSource.totalObligations || 0) }}</span>
                </div>
                <div class="stat-item" @click="$emit('view-disbursement', fundSource)">
                  <span class="stat-label">Total Disbursements</span>
                  <span class="stat-value disbursements">{{ formatPercentage(fundSource.totalDisbursement, fundSource.balance) }}</span>
                  <span class="stat-amount">{{ formatCurrency(fundSource.totalDisbursement || 0) }}</span>
                </div>
              </div>
            </div>
            <button class="primary-btn" @click="$emit('view-transactions', fundSource)">
              <i class="bx bx-receipt"></i> View Transactions
            </button>
          </div>
        </div>
      </div>

      <!-- Navigation dots -->
      <div class="slider-dots" v-if="fundSources.length > 1">
        <button 
          v-for="(dot, index) in fundSources" 
          :key="index"
          class="dot" 
          :class="{ active: index === currentSlide }"
          @click="goToSlide(index)"
        ></button>
      </div>
    </div>
  </div>
</template>



<style scoped>
.fund-sources-slider {
  width: 100%;
  position: relative;
  padding: 0;
  margin: 0;
  /* cursor: grab; */
}

.slider-container {
  position: relative;
  overflow: visible;
  width: 100%;
  max-width: 100%;
}

.slider-wrapper {
  display: flex;
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  touch-action: pan-y;
  gap: 40px; /* Increased gap proportionally */
  padding: 0 20px;
}

.card-slide {
  flex: 0 0 600px; /* Doubled from 300px to 600px */
  width: 600px; /* Doubled from 300px to 600px */
  transition: all 0.4s ease;
  transform: scale(0.9);
  opacity: 0.6;
}

.card-slide.active {
  transform: scale(1);
  opacity: 1;
}

.fund-card {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 50%, #c084fc 100%);
  color: white;
  padding: 30px; /* Doubled from 24px to 48px */
  min-height: 450px;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(139, 92, 246, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
}

.fund-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  pointer-events: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}

.fund-info h3.fund-name {
  font-size: 24px; /* Increased from 18px to 24px */
  font-weight: 700;
  margin: 0 0 8px 0; /* Increased margin */
  line-height: 1.2;
  color: #ffffff;
}

.fund-type {
  font-size: 16px; /* Increased from 12px to 16px */
  opacity: 0.85;
  color: #e0e7ff;
  font-weight: 400;
}

.action-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 8px;
  color: white;
  padding: 4px 10px; /* Increased padding */
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.expiry-section, .account-section, .cash-section, .transactions-section {
  margin-bottom: 15px; /* Increased margin */
  position: relative;
  /* z-index: 1; */
}

.expiry-label, .account-label, .cash-label, .transactions-label {
  display: block;
  font-size: 14px; /* Increased from 11px to 14px */
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
  margin-bottom: 2px; /* Increased margin */
  color: #e0e7ff;
  font-weight: 600;
}

.expiry-date {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.date-text {
  font-size: 18px; /* Increased from 15px to 18px */
  font-weight: 600;
  color: #ffffff;
}

.status-badge {
  font-size: 11px; /* Increased from 9px to 11px */
  padding: 8px 12px; /* Increased padding */
  border-radius: 12px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.status-active {
  background: #10b981;
  color: white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.status-expired {
  background: #ef4444;
  color: white;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.bank-info {
  display: flex;
  flex-direction: column;
  gap: 2px; /* Increased gap */
}

.bank-name, .account-number {
  font-size: 16px; /* Increased from 13px to 16px */
  color: #f8fafc;
  font-weight: 500;
}

.cash-amount {
  font-size: 36px; /* Increased from 28px to 36px */
  font-weight: 800;
  color: #fbbf24;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  line-height: 1;
}

.transactions-section {
  flex: 1;
}

.transaction-stats {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px; 
  width: 50%;
  transition: all 0.5s ease;
  cursor: pointer;
  padding-block: 12px;
}
.stat-item:not(:last-child) {
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  padding-right: 20px; /* Increased padding */
}
.stat-item:hover{
  background: rgba(255, 255, 255, 0.1);
}

.stat-label {
  font-size: 14px; /* Increased from 11px to 14px */
  opacity: 0.8;
  color: #e0e7ff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 18px; /* Increased from 15px to 18px */
  font-weight: 700;
}

.stat-value.obligations {
  color: #60a5fa;
}

.stat-value.disbursements {
  color: #e942a3;
}

.stat-amount {
  font-size: 15px; /* Increased from 12px to 15px */
  opacity: 0.9;
  color: #f1f5f9;
  font-weight: 500;
}

.card-footer {
  margin-top: auto;
  padding-top: 28px; /* Increased padding */
  position: relative;
  z-index: 1;
}

.primary-btn {
  width: 100%;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 16px 20px; /* Increased padding */
  border-radius: 12px;
  font-size: 16px; /* Increased from 14px to 16px */
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(20px);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.primary-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.slider-dots {
  display: flex;
  justify-content: center;
  gap: 16px; /* Increased gap */
  margin-top: 40px; /* Increased margin */
  padding: 0 20px;
}

.dot {
  width: 10px; /* Increased from 8px to 10px */
  height: 10px; /* Increased from 8px to 10px */
  border-radius: 50%;
  border: none;
  background: rgba(139, 92, 246, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background: #8b5cf6;
  transform: scale(1.5);
  box-shadow: 0 0 12px rgba(139, 92, 246, 0.6);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .card-slide {
    flex: 0 0 500px; /* Scaled down for smaller screens */
    width: 500px;
  }
  
  .fund-card {
    padding: 36px;
  }
  
  .slideWidth {
    width: 540px; /* Adjust slide width accordingly */
  }
}

@media (max-width: 768px) {
  .slider-wrapper {
    gap: 30px;
    padding: 0 15px;
  }
  
  .card-slide {
    flex: 0 0 400px; /* Further scaled down */
    width: 400px;
  }
  
  .fund-card {
    padding: 28px;
    min-height: 480px;
  }
  
  .fund-name {
    font-size: 20px !important;
  }
  
  .cash-amount {
    font-size: 30px;
  }
}

@media (max-width: 480px) {
  .slider-wrapper {
    gap: 20px;
    padding: 0 10px;
  }
  
  .card-slide {
    flex: 0 0 320px; /* Back to original size for mobile */
    width: 320px;
  }
  
  .card-slide.active {
    transform: scale(1);
  }
  
  .card-slide {
    transform: scale(0.95);
  }
  
  .fund-card {
    padding: 24px;
  }
  
  .fund-name {
    font-size: 18px !important;
  }
  
  .cash-amount {
    font-size: 24px;
  }
}

/* Animation for card entrance */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(0.9);
  }
}

@keyframes slideInUpActive {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.card-slide {
  animation: slideInUp 0.6s ease-out;
}

.card-slide.active .fund-card {
  animation: slideInUpActive 0.6s ease-out;
}
</style>